import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Loader2 } from "lucide-react";
import { po, money, backendUrl } from "./api";

const iso = (d) => d.toISOString().slice(0, 10);
const RANGES = {
  today: () => { const t = iso(new Date(Date.now() + 330 * 60000)); return [t, t]; },
  week: () => { const n = new Date(Date.now() + 330 * 60000); const s = new Date(n); s.setDate(n.getDate() - 6); return [iso(s), iso(n)]; },
  month: () => { const n = new Date(Date.now() + 330 * 60000); return [iso(new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), 1))), iso(n)]; },
};

function Rows({ rows, label }) {
  if (!rows.length) return <p className="text-muted-foreground">No pickup orders in this period.</p>;
  return (
    <div className="overflow-x-auto"><table className="w-full text-base">
      <thead><tr className="text-left text-[#7a0f1d]"><th className="p-2">{label}</th><th className="p-2 text-right">Orders</th><th className="p-2 text-right">Paid</th><th className="p-2 text-right">Collected</th><th className="p-2 text-right">Unpaid orders</th><th className="p-2 text-right">Unpaid amount</th><th className="p-2 text-right">Cancelled</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.center || `${r.date}${r.currency_symbol}`} className="border-t" data-testid={`po-report-row-${r.center || `${r.date}-${r.currency_symbol === "₹" ? "INR" : "AUD"}`}`}>
          <td className="p-2">{r.center ? r.label : `${r.date}${r.currencies > 1 ? ` (${r.currency_symbol})` : ""}`}</td><td className="p-2 text-right">{r.orders}</td><td className="p-2 text-right">{r.paid_orders}</td>
          <td className="p-2 text-right text-emerald-800">{money(r.currency_symbol, r.collected_amount)}</td>
          <td className="p-2 text-right">{r.unpaid_orders}</td><td className="p-2 text-right text-amber-800">{money(r.currency_symbol, r.unpaid_amount)}</td><td className="p-2 text-right">{r.cancelled}</td>
        </tr>))}</tbody>
    </table></div>
  );
}

export function PickupReport({ token, center, onClose }) {
  const [[from, to], setRange] = useState(RANGES.today());
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); po("report", token, { center: center || "all", from_date: from, to_date: to }).then(setD).catch((e) => toast.error(e.message)); }, [token, center, from, to]);
  const dl = async (kind) => {
    const r = await fetch(`${backendUrl}/api/pickup-orders/report/${kind}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, center: center || "all", from_date: from, to_date: to }) });
    if (!r.ok) return toast.error("Download failed");
    const a = document.createElement("a"); a.href = URL.createObjectURL(await r.blob()); a.download = `pickup_report_${from}_${to}.${kind}`; a.click();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="po-report-dialog">
        <DialogHeader><DialogTitle>Pickup report {center ? `— ${center}` : "— all centres"}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-end gap-2">
          {Object.keys(RANGES).map((k) => <Button key={k} variant="outline" className="h-11 capitalize" onClick={() => setRange(RANGES[k]())} data-testid={`po-report-${k}`}>{k === "week" ? "Last 7 days" : k === "month" ? "This month" : "Today"}</Button>)}
          <div><Label>From</Label><Input type="date" className="h-11" value={from} onChange={(e) => setRange([e.target.value, to])} data-testid="po-report-from" /></div>
          <div><Label>To</Label><Input type="date" className="h-11" value={to} onChange={(e) => setRange([from, e.target.value])} data-testid="po-report-to" /></div>
          <Button className="h-11" onClick={() => dl("xlsx")} data-testid="po-report-xlsx"><Download className="mr-1 h-4 w-4" />Excel</Button>
          <Button className="h-11" variant="outline" onClick={() => dl("pdf")} data-testid="po-report-pdf"><Download className="mr-1 h-4 w-4" />PDF</Button>
        </div>
        {!d ? <Loader2 className="mx-auto h-8 w-8 animate-spin" /> : (<>
          <h3 className="mt-2 text-lg font-semibold">By centre</h3><Rows rows={d.centres} label="Centre" />
          <h3 className="mt-2 text-lg font-semibold">By day</h3><Rows rows={d.days} label="Date" />
        </>)}
      </DialogContent>
    </Dialog>
  );
}
